import { useEffect, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';

import { TaskData } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { getIds } from '@utils/getIds';
import { userPracticeAssistantQueryOptions } from '@modules/user/queries/userPracticeAssistantQueries';

import { taskQueryOptions } from '@common/Task/queries/taskQueries';
import type { TaskFormActions } from '../types';
import { loadTaskAssistants } from '../utils/taskRelations';

interface Params {
    taskId?: string;
    patientId?: string;
    medicalCaseIds?: string[];
    entryId?: string;
}

interface Data {
    task?: TaskData;
    isLoading: boolean;
    syncTaskBaseline(patch: Partial<TaskData>): void;
}

// Порт TaskService.initData на RQ + контекст: грузит задачу (RQ), резолвит ассистентов, сидит форму
// и вычисляет права/дефолты. Данные пациента и медкейсов грузят RQ-хуки формы по id из state.
// Работает от загруженной задачи (setState асинхронный — читать форму сразу после seed нельзя).
export function useTaskInit(params: Params, actions: TaskFormActions): Data {
    const queryClient = useQueryClient();
    const [task, setTask] = useState<TaskData>();
    const [isLoading, setIsLoading] = useState(true);

    const { taskId, patientId, medicalCaseIds, entryId } = params;
    const medicalCaseIdsKey = (medicalCaseIds ?? []).join(',');

    useEffect(() => {
        let active = true;
        setIsLoading(true);

        void (async () => {
            const loaded = await initTaskForm(
                { taskId, patientId, medicalCaseIds: medicalCaseIdsKey ? medicalCaseIdsKey.split(',') : [], entryId },
                actions,
                queryClient,
            );

            if (active) {
                setTask(loaded);
                setIsLoading(false);
            }
        })();

        return () => {
            active = false;
            actions.reset();
        };
    }, [taskId, patientId, medicalCaseIdsKey, entryId, actions, queryClient]);

    // Синхронизируем baseline после частичного сохранения (inline-save комментария), чтобы
    // следующий diff в getTaskUpdateParams не считал уже сохранённое поле изменённым.
    const syncTaskBaseline = (patch: Partial<TaskData>): void => {
        setTask(prev => (prev ? { ...prev, ...patch } : prev));
    };

    return { task, isLoading, syncTaskBaseline };
}

async function initTaskForm(
    params: Params,
    actions: TaskFormActions,
    queryClient: QueryClient,
): Promise<TaskData | undefined> {
    const { authUser } = store;
    const { taskId, patientId: initPatientId, medicalCaseIds: initMedicalCaseIds, entryId: initEntryId } = params;

    const task = taskId ? await queryClient.fetchQuery(taskQueryOptions.detail(taskId)) : undefined;

    if (task) {
        actions.seed(task);
    } else {
        actions.reset();
    }

    actions.setUserPermissions(authUser.id, await loadTaskAssistants(authUser.id, task, queryClient));

    actions.setParticipant('author', task?.authorId ?? authUser.id);
    actions.setParticipant('requester', task?.requesterId ?? authUser.id);

    const patientId = initPatientId ?? task?.patientId;
    const medicalCaseIds = initMedicalCaseIds ?? task?.medicalCaseIds ?? [];
    const entryId = initEntryId ?? task?.parentEntryId;

    if (patientId) {
        actions.setPatientDataField('patient', patientId);
    }

    if (medicalCaseIds.length > 0) {
        actions.setMedicalCaseIds(medicalCaseIds);
    }

    if (entryId) {
        actions.setPatientDataField('entry', entryId);
    }

    if (!taskId) {
        const practiceAssistants = await queryClient.fetchQuery(
            userPracticeAssistantQueryOptions.byUserId(authUser.id),
        );
        const practiceAssistantIds = getIds(practiceAssistants);
        actions.setExecutors(isEmpty(practiceAssistantIds) ? [authUser.id] : practiceAssistantIds);
    }

    return task;
}
